import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import cn from 'classnames';
import {
  AdminNoteTypeOptions,
  buildNoteMessages,
  getAdminNoteTemplate,
  groupNoteFields,
  nextContactAttempt,
} from 'constants/admin-note-templates';
import { CheckHistoryTypes } from 'constants/check-history-types';
import { Controller, useForm } from 'react-hook-form';

import {
  Button,
  DatePicker,
  Input,
  InputGroup,
  Select,
  Textarea,
  Toggle,
  ValidationAlert,
} from 'components/FormComponents';

const RequiredAlert = ({ message }) =>
  message ? (
    <div className="mt-2">
      <ValidationAlert show message={message} />
    </div>
  ) : null;

const fieldLabel = (field, values) =>
  typeof field.label === 'function' ? field.label(values) : field.label;

const visibleFields = (fields, values, context) =>
  (fields || []).filter(field => !field.when || field.when(values, context));

const fieldDefault = (field, context) => {
  if (typeof field.defaultValue === 'function') return field.defaultValue(context) || '';
  return field.defaultValue ?? '';
};

const countsAttempt = who => ['employer', 'provider', 'institution'].includes(who);

const fieldNodes = (fields, { register, control, errors, values }) =>
  groupNoteFields(fields).map(group => {
    const nodes = group.map(field => {
      const error = errors?.[field.name]?.message;
      const label = fieldLabel(field, values);
      if (field.type === 'select') {
        return (
          <InputGroup key={field.name} title={label} optional={!field.required}>
            <Select
              register={register}
              name={field.name}
              value={values?.[field.name] || ''}
              useDefault={false}
              placeholder="Select"
              validators={field.required ? { required: 'Required' } : undefined}
              error={error}
              noErrorMessage>
              {(field.options || []).map(option => (
                <Select.Item key={option.value} value={option.value}>
                  {option.name}
                </Select.Item>
              ))}
            </Select>
            <RequiredAlert message={error} />
          </InputGroup>
        );
      }
      if (field.type === 'textarea') {
        return (
          <InputGroup key={field.name} title={label} optional={!field.required}>
            <Textarea
              register={register}
              name={field.name}
              validators={field.required ? { required: 'Required' } : undefined}
              placeholder={field.placeholder}
              className="d-block"
              inputProps={{ rows: 3 }}
            />
            <RequiredAlert message={error} />
          </InputGroup>
        );
      }
      if (field.type === 'date') {
        return (
          <InputGroup
            key={field.name}
            title={label}
            optional={!field.required}
            className="flex-grow-1">
            <Controller
              control={control}
              name={field.name}
              rules={field.required ? { required: 'Required' } : undefined}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  value={value}
                  onChange={onChange}
                  useDefaultToday={false}
                  outputFormat="DD/MM/YYYY"
                  error={error || null}
                  noErrorMessage
                />
              )}
            />
            <RequiredAlert message={error} />
          </InputGroup>
        );
      }
      return (
        <InputGroup
          key={field.name}
          title={label}
          optional={!field.required}
          className="flex-grow-1">
          <Input
            register={register}
            name={field.name}
            type={field.type === 'time' ? 'time' : field.type === 'number' ? 'number' : 'text'}
            validators={field.required ? { required: 'Required' } : undefined}
            error={error}
            noErrorMessage
            placeholder={field.placeholder}
          />
          <RequiredAlert message={error} />
        </InputGroup>
      );
    });

    if (group.length === 1) return nodes;
    return (
      <div key={group.map(field => field.name).join('-')} className="d-flex u-gap">
        {nodes}
      </div>
    );
  });

export const AdminNoteForm = forwardRef(
  (
    {
      tagDefault = CheckHistoryTypes.AdminGeneral,
      initial,
      checkTypeSlug,
      notes = [],
      onSubmit = () => {},
      onCancel = () => {},
      className = '',
      actionLabel = 'Add note',
      cancelLabel = 'Cancel',
      showCancel = true,
    },
    ref,
  ) => {
    const initialType = String(initial?.type || tagDefault);
    const startingType = AdminNoteTypeOptions.some(option => option.value === initialType)
      ? initialType
      : '';
    const context = { checkTypeSlug, notes };
    const buildValues = (noteType, existing = {}) => {
      const template = getAdminNoteTemplate(noteType);
      const next = {
        type: String(noteType),
        share_with_client: existing.share_with_client ?? template?.share === 'auto',
        intercom_id: existing.intercom_id || '',
        client_message: existing.client_message || '',
      };
      (template?.fields || []).forEach(field => {
        const current = existing[field.name];
        next[field.name] =
          current != null && current !== '' ? current : fieldDefault(field, context);
      });
      if (countsAttempt(next.who) && !next.attempt) {
        next.attempt = nextContactAttempt(notes);
      }
      return next;
    };
    const {
      register,
      control,
      handleSubmit,
      watch,
      reset,
      getValues,
      setValue,
      unregister,
      formState: { errors },
    } = useForm({
      defaultValues: buildValues(startingType, {
        share_with_client: initial?.share_with_client,
        intercom_id: initial?.intercom_id || initial?.fields?.intercom_id || '',
        client_message: initial?.client_message || '',
        ...(initial?.fields || {}),
      }),
    });

    const type = watch('type');
    const lastType = useRef(type);
    const autoAttempt = useRef(null);
    const attemptSeeded = useRef(false);
    const clientLocked = useRef(Boolean(String(initial?.client_message || '').trim()));

    useEffect(() => {
      if (String(lastType.current) === String(type)) return;
      lastType.current = type;
      clientLocked.current = false;
      const nextValues = buildValues(type, {
        intercom_id: getValues('intercom_id') || '',
      });
      reset(nextValues);
      autoAttempt.current = countsAttempt(nextValues.who) ? String(nextValues.attempt || '') : null;
    }, [type, getValues, reset, checkTypeSlug, notes]);

    const resetForm = () => {
      const nextType = String(tagDefault);
      lastType.current = nextType;
      clientLocked.current = false;
      const nextValues = buildValues(nextType);
      reset(nextValues);
      autoAttempt.current = countsAttempt(nextValues.who) ? String(nextValues.attempt || '') : null;
    };

    useImperativeHandle(ref, () => ({
      reset: resetForm,
    }));

    const handleCancel = event => {
      event.preventDefault();
      resetForm();
      onCancel();
    };

    const template = getAdminNoteTemplate(type);
    const values = watch();
    const shownFields = visibleFields(template?.fields, values, context);

    useEffect(() => {
      const current = getValues();
      const visible = new Set(
        visibleFields(template?.fields, current, context).map(field => field.name),
      );
      (template?.fields || []).forEach(field => {
        if (!visible.has(field.name)) unregister(field.name);
      });
    }, [
      type,
      values.who,
      values.method,
      values.purpose,
      values.source,
      template,
      getValues,
      unregister,
      checkTypeSlug,
    ]);
    const sharing = template?.share === 'auto' || !!values.share_with_client;
    const visibleValues = {};
    shownFields.forEach(field => {
      visibleValues[field.name] = values[field.name];
    });
    const { clientMessage: generatedClientMessage } = buildNoteMessages(
      type,
      visibleValues,
      context,
    );
    const clientRegister = register(
      'client_message',
      sharing ? { required: 'Required' } : undefined,
    );

    useEffect(() => {
      if (!sharing || clientLocked.current) return;
      if ((getValues('client_message') || '') === generatedClientMessage) return;
      setValue('client_message', generatedClientMessage);
    }, [generatedClientMessage, sharing, getValues, setValue]);

    useEffect(() => {
      if (!attemptSeeded.current) {
        attemptSeeded.current = true;
        if (countsAttempt(getValues('who')) && getValues('attempt') && !initial?.fields?.attempt) {
          autoAttempt.current = String(getValues('attempt'));
        }
      }
      if (!countsAttempt(values.who)) return;
      const current = String(values.attempt || '');
      if (current && current !== String(autoAttempt.current ?? '')) return;
      const next = String(nextContactAttempt(notes));
      if (current === next) return;
      autoAttempt.current = next;
      setValue('attempt', next);
    }, [values.who, values.attempt, notes, setValue, getValues, initial]);

    const submit = data => {
      const noteType = Number(data.type);
      const noteTemplate = getAdminNoteTemplate(noteType);
      const fields = {};
      visibleFields(noteTemplate?.fields, data, context).forEach(field => {
        const fieldValue = String(data[field.name] ?? '').trim();
        if (fieldValue) fields[field.name] = fieldValue;
      });
      const built = buildNoteMessages(noteType, fields, context);
      const clientText = String(data.client_message || '').trim();
      const wantsShare = noteTemplate?.share === 'auto' || !!data.share_with_client;
      const share = wantsShare && !!clientText;
      const intercomId =
        noteType === CheckHistoryTypes.AdminContact
          ? data.method === 'team_inbox'
            ? (fields.intercom_id || '').trim() || null
            : null
          : (data.intercom_id || '').trim() || null;
      onSubmit({
        type: noteType,
        message: built.message,
        client_message: share ? clientText : '',
        share_with_client: share,
        pin_for_client: !!(share && noteTemplate?.pin),
        metadata: {
          fields,
          intercom_id: intercomId,
          shared: share,
          client_message: share ? clientText : null,
        },
      });
    };

    return (
      <div className={cn(className)}>
        <form onSubmit={handleSubmit(submit)} className="d-flex flex-column u-gap u-margin-top">
          <InputGroup title="Type">
            <Select
              register={register}
              name="type"
              value={type}
              useDefault={!!startingType}
              defaultValue={startingType}
              validators={{ required: 'Required' }}>
              {AdminNoteTypeOptions.map(option => (
                <Select.Item key={option.value} value={option.value}>
                  {option.name}
                </Select.Item>
              ))}
            </Select>
          </InputGroup>
          {template && fieldNodes(shownFields, { register, control, errors, values })}
          {template?.share === 'optional' && (
            <label className="d-flex align-items-center u-gap u-margin-bottom--0">
              <Toggle name="share_with_client" register={register} small />
              <span className="u-font-size-small">Also add a note the client can see</span>
            </label>
          )}
          {sharing && (
            <div className="d-flex flex-column u-gap-small">
              <InputGroup
                title={
                  template?.pin
                    ? 'Client will see (pinned on their history until they unpin it)'
                    : 'Client will see'
                }>
                <Textarea
                  register={() => clientRegister}
                  name="client_message"
                  placeholder="Message the client will see"
                  className="d-block"
                  inputProps={{
                    rows: 3,
                    onChange: event => {
                      clientLocked.current = true;
                      clientRegister.onChange(event);
                    },
                  }}
                />
                <RequiredAlert message={errors?.client_message?.message} />
              </InputGroup>
            </div>
          )}
          <div className="u-flex-align-self-end d-flex u-gap">
            {showCancel && (
              <Button type="secondary" small onClick={handleCancel}>
                {cancelLabel}
              </Button>
            )}
            <Button submit small>
              {actionLabel}
            </Button>
          </div>
        </form>
      </div>
    );
  },
);
